import { useEffect, useState } from "react";
import { GetForecastLocksQuery, GetUtsvOperationalMetricsForPropertyYearQuery, OriginalRevenueOperationalMetricValidKey, UnitTypeScheduleValueUpsertInput, useGetForecastLocksLazyQuery, useGetUtsvOperationalMetricsForPropertyYearLazyQuery, useUpsertUnitTypeScheduleValuesMutation, VersionType } from "../../../../__generated__/generated_types";
import { useVersions } from "../../../../contexts/versions/VersionsContext";
import { Property } from "../../../../contexts/properties/logic";
import {IVZHotTableRow} from "../../../../components/vz-ui/vz-hot-table/VZHotTable";


export interface IBatchUpsertInput {
    fieldName: OriginalRevenueOperationalMetricValidKey;
    unitTypeName: string;
    monthIndex: number;
    value: string;
}

interface IUseUTSVOperationalMetrics {
    property?: Property;
    year: number;
    versionType: VersionType;
}

interface IUseUTSVOperationalMetricsReturn {
    fetchMetricData: () => void;
    metricData?: GetUtsvOperationalMetricsForPropertyYearQuery["originalRevenueUTSVOperationalMetricsForPropertyYear"];
    metricDataLoading: boolean;
    forecastLocks?: GetForecastLocksQuery;
    lastSaved: Date;
    savingValues: boolean;
    upsertValues: (data: {label: string}[], changes: [number, number, number | null][], budgetYear: number, metric: OriginalRevenueOperationalMetricValidKey) => Promise<boolean>;
}

const metricFieldMap = new Map<OriginalRevenueOperationalMetricValidKey, string>([
    [OriginalRevenueOperationalMetricValidKey.AverageMarketRent, "averageMarketRent"],
    [OriginalRevenueOperationalMetricValidKey.RenewalRatio, "renewalRatio"],
    [OriginalRevenueOperationalMetricValidKey.MoveOutRate, "moveOutRate"],
    [OriginalRevenueOperationalMetricValidKey.OccupancyRate, "occupancyRate"],
    [OriginalRevenueOperationalMetricValidKey.LeaseExpirationCount, "leaseExpirationCount"],
    [OriginalRevenueOperationalMetricValidKey.RenewalRate, "renewalRate"],
    [OriginalRevenueOperationalMetricValidKey.EarlyTerminationCount, "earlyTerminationCount"],
    [OriginalRevenueOperationalMetricValidKey.MoveOutCount, "moveOutCount"],
    [OriginalRevenueOperationalMetricValidKey.RenewalCount, "renewalCount"],
    [OriginalRevenueOperationalMetricValidKey.MonthToMonthCount, "monthToMonthCount"],
    [OriginalRevenueOperationalMetricValidKey.MonthToMonthMoveOutCount, "monthToMonthMoveOutCount"],
    [OriginalRevenueOperationalMetricValidKey.UserSpecifiedUnitCount, "userSpecifiedUnitCount"],
]);

const metricsNeedingPercentConversion = [
    OriginalRevenueOperationalMetricValidKey.RenewalRatio,
    OriginalRevenueOperationalMetricValidKey.MoveOutRate,
    OriginalRevenueOperationalMetricValidKey.OccupancyRate,
];

export function useUTSVOperationalMetrics({property, year, versionType}: IUseUTSVOperationalMetrics): IUseUTSVOperationalMetricsReturn {
    const {versions} = useVersions();

    const [lastSaved, setLastSaved] = useState<Date>(new Date());

    const [getForecastLocks, { data: forecastLocksData }] = useGetForecastLocksLazyQuery({fetchPolicy: "no-cache"});
    const [getMetricData, {data: metricData, loading: metricDataLoading}] = useGetUtsvOperationalMetricsForPropertyYearLazyQuery({fetchPolicy: "no-cache"});
    const [upsertUnitTypeScheduleValues, {loading: savingValues}] = useUpsertUnitTypeScheduleValuesMutation({ignoreResults: true});

    useEffect(() => {
        if (property) {
            getForecastLocks({
                variables: {
                    propertyId: property.id,
                    budgetYear: property.budgetYear
                }
            });
            getMetricData({
                variables: {
                    propertyId: property.id,
                    budgetYear: property.budgetYear,
                }
            });
        }
    }, [property]);

    function fetchMetricData() {
        if (!property) {
            return;
        }

        getMetricData({
            variables: {
                propertyId: property.id,
                budgetYear: property.budgetYear,
            }
        });
    }

    async function upsertValues(data: Pick<IVZHotTableRow, "label">[], changes: [number, number, number | null][], budgetYear: number, metric: OriginalRevenueOperationalMetricValidKey): Promise<boolean> {
        const valuesPayload: UnitTypeScheduleValueUpsertInput[] = [];

        for (const [rowIndex, monthIndex, value] of changes) {
            const row = data[rowIndex];
            let adjustedValue = value === null ? null : value.toString();

            if (!row) {
                continue;
            }

            if (value !== null && value != 0 && metricsNeedingPercentConversion.includes(metric)) {
                adjustedValue = (value / 100.0).toString();
            }

            valuesPayload.push({
                unitTypeName: row.label,
                monthIndex,
                value: adjustedValue,
            });
        }

        const versionId = versions?.valueVersions.find(x => x.year == year && x.type == versionType);
        const fieldName = metricFieldMap.get(metric);

        if (property && versionId && fieldName) {
            return upsertUnitTypeScheduleValues({
                variables: {
                    propertyId: property.id,
                    versionId: versionId.id,
                    fieldName,
                    values: valuesPayload,
                    budgetYear: budgetYear
                },
            }).then((data) => {
                if (data.data?.upsertUnitTypeScheduleValues) {
                    setLastSaved(new Date());
                    return true;
                }
                return false;
            });
        } else {
            return false;
        }
    }

    return {
        fetchMetricData,
        metricData: metricData?.originalRevenueUTSVOperationalMetricsForPropertyYear,
        metricDataLoading,
        forecastLocks: forecastLocksData,
        lastSaved,
        savingValues,
        upsertValues,
    };
}
